import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { edgeDataApi, unwrapV2 } from './data-api'
import {
  PriceAlertListSchema,
  PriceAlertSchema,
  type PriceAlert,
  type PriceAlertCreate,
  type PriceAlertList,
  type PriceAlertStatusFilter,
} from '@/features/price-alerts/price-alert-types'

// ─── Price alerts (/v2/price-alerts) ───
//
// Injected rather than inlined in data-api.ts (the data-api inference cliff),
// like planner-api. Success bodies are plain JSON; unwrapV2 also tolerates the
// v2 envelope in case the middleware ever wraps them.

const BASE = '/v2/price-alerts'
export const PRICE_ALERTS_LIST_TAG = { type: 'PriceAlerts' as const, id: 'LIST' }

/** The price alerts list's fallback poll: realtime events cover fires and splits, the
 *  client clock covers expiry, and nothing covers a create or cancel made in another tab
 *  or on another device. */
export const PRICE_ALERTS_POLL_MS = 30_000
/** The list's one retry (spec C6) waits this long, as auth-fetch.ts:24-28 does for a 429. */
export const PRICE_ALERTS_RETRY_MS = 1_000

/** A network failure, a timeout or a 5xx: worth one more try. */
export function isRetryableError(error: FetchBaseQueryError): boolean {
  const s = error.status
  return s === 'FETCH_ERROR' || s === 'TIMEOUT_ERROR' || (typeof s === 'number' && s >= 500)
}

/** Run a request, and once more after a retryable failure. */
export async function withOneRetry<R extends { error?: FetchBaseQueryError }>(
  run: () => Promise<R>,
  wait: () => Promise<void>,
): Promise<R> {
  const first = await run()
  if (!first.error || !isRetryableError(first.error)) return first
  await wait()
  return run()
}

/** `{alerts:[...], limit, active}`, enveloped or not; a bare array is accepted too. */
export function parsePriceAlertList(raw: unknown): PriceAlertList {
  const body = unwrapV2<unknown>(raw)
  if (Array.isArray(body)) return PriceAlertListSchema.parse({ alerts: body })
  return PriceAlertListSchema.parse(body)
}

/** A DELETE's result for the cancel mutation. A 404 is a success with no body: the hourly
 *  sweep may have purged the row, and either way it is no longer active. */
export function cancelResult(res: {
  data?: unknown
  error?: FetchBaseQueryError
}): { data: PriceAlert | null } | { error: FetchBaseQueryError } {
  if (res.error) return res.error.status === 404 ? { data: null } : { error: res.error }
  return { data: PriceAlertSchema.parse(unwrapV2(res.data)) }
}

export const edgePriceAlertsApi = edgeDataApi.injectEndpoints({
  endpoints: (builder) => ({
    listPriceAlerts: builder.query<PriceAlertList, PriceAlertStatusFilter>({
      // One retry after a network or server error (spec C6); a second failure is the error.
      // The wait does not watch api.signal: a retry that lands after its last subscriber
      // left is simply discarded by RTK.
      async queryFn(status, _api, _extra, baseQuery) {
        const url = `${BASE}?status=${encodeURIComponent(status)}`
        const res = await withOneRetry(
          async () => baseQuery(url),
          () => new Promise((resolve) => setTimeout(resolve, PRICE_ALERTS_RETRY_MS)),
        )
        if (res.error) return { error: res.error }
        try {
          return { data: parsePriceAlertList(res.data) }
        } catch (e) {
          return {
            error: { status: 'CUSTOM_ERROR', error: e instanceof Error ? e.message : String(e) },
          }
        }
      },
      providesTags: [PRICE_ALERTS_LIST_TAG],
    }),

    createPriceAlert: builder.mutation<PriceAlert, PriceAlertCreate>({
      query: (body) => ({ url: BASE, method: 'POST', body }),
      transformResponse: (raw) => PriceAlertSchema.parse(unwrapV2(raw)),
      invalidatesTags: [PRICE_ALERTS_LIST_TAG],
    }),

    cancelPriceAlert: builder.mutation<PriceAlert | null, { id: string }>({
      async queryFn({ id }, _api, _extra, baseQuery) {
        const res = await baseQuery({ url: `${BASE}/${encodeURIComponent(id)}`, method: 'DELETE' })
        try {
          return cancelResult(res)
        } catch (e) {
          return {
            error: { status: 'CUSTOM_ERROR', error: e instanceof Error ? e.message : String(e) },
          }
        }
      },
      invalidatesTags: [PRICE_ALERTS_LIST_TAG],
    }),
  }),
})

export const { useListPriceAlertsQuery, useCreatePriceAlertMutation, useCancelPriceAlertMutation } =
  edgePriceAlertsApi

/**
 * Human text for a failed price-alert call (an RTK `unwrap()` rejection).
 * Errors arrive in the app envelope `{success:false, error:{code,message}}`;
 * a FastAPI-style `{detail}` (string or a 422 issue list) is read too.
 */
export function priceAlertErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { data?: unknown } | null)?.data
  if (data && typeof data === 'object') {
    const d = data as { error?: { message?: unknown }; detail?: unknown; message?: unknown }
    if (typeof d.error?.message === 'string' && d.error.message) return d.error.message
    if (typeof d.detail === 'string' && d.detail) return d.detail
    if (Array.isArray(d.detail)) {
      const msgs = d.detail
        .map((i) => (i && typeof i === 'object' ? (i as { msg?: unknown }).msg : null))
        .filter((m): m is string => typeof m === 'string' && m.length > 0)
      if (msgs.length) return msgs.join('; ')
    }
    if (typeof d.message === 'string' && d.message) return d.message
  }
  return fallback
}
